import parse from 'html-react-parser';
import { useEffect, useState } from 'react';

import { Loader } from '@/components/core/Loader';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/core/Table';

import {
    ComparedItemsIterface,
    CompareItemsDialogTableProps,
} from './compare-items-dialog-types';

const CompareItemsDialogTable = ({
    items,
    comparedItems,
    isShowDifferentOnly,
    isShowFilledOnly,
}: CompareItemsDialogTableProps) => {
    const notSimilarBgColor = '!tw-bg-[#F3D9D9]';
    const similarBgColor = '!tw-bg-[#E0FBF9]';
    const [filteredItems, setFilteredItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        const getTableRows = (attribute: any, val1: any, val2: any) => {
            const label = attribute.label;
            if (!isLostAndFound(attribute.name)) {
                switch (attribute.comparisonTemplate) {
                    case 'TABLE':
                        return compareTableComponent(label, attribute);
                    case 'IMAGE':
                        return compareImageComponent(label, val1, val2);
                    case 'DIMENSIONAL':
                        return DimensionalComparison(label, val1, val2);
                    case 'TEXT':
                        return compareTextComponent(label, val1, val2);
                    default:
                        return compareTextComponent(label, val1, val2);
                }
            }
        };

        const compareTableComponent = (label: string, a: any) => {
            const { name, members, collections } = a;
            const renderTableRows = (_collections: any) => {
                return (
                    <div className="tw-w-[320px] tw-whitespace-nowrap tw-overflow-x-auto tw-p-4">
                        <Table>
                            <TableHeader>
                                {members?.map((memberAttribute: any) => (
                                    <TableHead
                                        className="tw-font-bold tw-text-t-sm"
                                        key={memberAttribute.name}
                                    >
                                        {memberAttribute.label}
                                    </TableHead>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {_collections.map(
                                    (collection: any, index: number) => {
                                        return (
                                            <TableRow key={index}>
                                                {members?.map(
                                                    (memberAttribute: any) => {
                                                        const bgColor =
                                                            collection[
                                                                memberAttribute
                                                                    .name
                                                            ]?.isEqual
                                                                ? similarBgColor
                                                                : notSimilarBgColor;
                                                        return (
                                                            <TableCell
                                                                className={`tw-w-1/${_collections.length} ${bgColor}`}
                                                                key={
                                                                    memberAttribute.name
                                                                }
                                                            >
                                                                {
                                                                    collection[
                                                                        memberAttribute
                                                                            .name
                                                                    ]?.val
                                                                }
                                                            </TableCell>
                                                        );
                                                    },
                                                )}
                                            </TableRow>
                                        );
                                    },
                                )}
                            </TableBody>
                        </Table>
                    </div>
                );
            };
            return (
                <TableRow>
                    <TableCell className="tw-capitalize tw-w-1/3">
                        {label}
                    </TableCell>
                    <TableCell className="tw-w-1/3 tw-pl-0">
                        {collections.first[name].length > 0 &&
                            renderTableRows(collections.first[name])}
                    </TableCell>
                    <TableCell className="tw-w-1/3 tw-pl-0">
                        {collections.first[name].length > 0 &&
                            renderTableRows(collections.second[name])}
                    </TableCell>
                </TableRow>
            );
        };

        const compareTextComponent = (label: string, val1: any, val2: any) => {
            const bgColor = val1 !== val2 ? notSimilarBgColor : similarBgColor;
            return (
                <TableRow>
                    <TableCell className="tw-capitalize tw-w-1/3">
                        {label}
                    </TableCell>
                    <TableCell
                        className={`tw-w-1/3 tw-whitespace-pre-line ${bgColor} first-item`}
                    >
                        {isHTML(val1) ? (
                            <div>{parse(val1)}</div>
                        ) : (
                            <div
                                style={{
                                    wordWrap: 'break-word',
                                    overflowWrap: 'anywhere',
                                }}
                                className="tw-whitespace-pre-line"
                            >
                                {val1}
                            </div>
                        )}
                    </TableCell>

                    <TableCell
                        className={`tw-w-1/3 tw-whitespace-pre-line ${bgColor} second-item`}
                    >
                        {isHTML(val2) ? (
                            <div>{parse(val2)}</div>
                        ) : (
                            <div
                                style={{
                                    wordWrap: 'break-word',
                                    overflowWrap: 'anywhere',
                                }}
                                className="tw-whitespace-pre-line"
                            >
                                {val2}
                            </div>
                        )}
                    </TableCell>
                </TableRow>
            );
        };

        const DimensionalComparison = (
            label: string,
            firstValue: any,
            secondValue: any,
        ) => {
            const itemsToDisplay = isShowDifferentOnly
                ? Object.entries(firstValue).filter(
                    ([key, value]) => value !== secondValue[key],
                )
                : Object.entries(firstValue);
            return (
                <>
                    {itemsToDisplay.map(([key, value]: [any, any]) => {
                        const bgColor =
                            value !== secondValue[key]
                                ? notSimilarBgColor
                                : similarBgColor;
                        return (
                            <TableRow key={key}>
                                <TableCell className="tw-capitalize tw-w-1/3 tw-whitespace-pre-line">
                                    {label} ({key})
                                </TableCell>
                                <TableCell
                                    className={`tw-capitalize tw-w-1/3 tw-whitespace-pre-line tw-break-all ${bgColor}`}
                                >
                                    {value ?? '-'}
                                </TableCell>
                                <TableCell
                                    className={`tw-capitalize tw-w-1/3 tw-whitespace-pre-line tw-break-all ${bgColor}`}
                                >
                                    {secondValue[key] ?? '-'}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </>
            );
        };
        const compareImageComponent = (label: string, val1: any, val2: any) => {
            return (
                <TableRow>
                    <TableCell className="tw-capitalize tw-w-1/3">
                        {label}
                    </TableCell>
                    <TableCell className="tw-w-1/3">
                        {val1 && (
                            <img
                                className="item-image itemImage-large"
                                src={val1}
                                alt={val1}
                            />
                        )}
                    </TableCell>
                    <TableCell className="tw-w-1/3">
                        {val2 && (
                            <img
                                className="item-image itemImage-large"
                                src={val2}
                                alt={val2}
                            />
                        )}
                    </TableCell>
                </TableRow>
            );
        };
        const processedItems = comparedItems
            .map((_items: ComparedItemsIterface) => {
                return {
                    tableRows: getTableRows(
                        _items.attribute,
                        _items.firstItem,
                        _items.secondItem,
                    ),
                    isHtml:
                        isHTML(_items.firstItem) || isHTML(_items.secondItem),
                    isEmpty:
                        isEmpty(_items.firstItem) && isEmpty(_items.secondItem),
                    isDifferent:
                        JSON.stringify(_items.firstItem) !==
                        JSON.stringify(_items.secondItem),
                };
            })
            .filter(
                (item) =>
                    (!isShowDifferentOnly || item.isDifferent) &&
                    (!isShowFilledOnly || !item.isEmpty),
            );

        setFilteredItems(processedItems);
        setLoading(false);
    }, [comparedItems, isShowDifferentOnly, isShowFilledOnly]);

    useEffect(() => {
        setLoading(false);
    }, [filteredItems]);

    const isEmpty = (str: string) => {
        return !str || (typeof str === 'string' && str.trim() === '');
    };

    const isHTML = (str: string) => {
        const htmlRegex = /<([A-Z][A-Z0-9]*)\b[^>]*>(.*?)<\/\1>/i;
        return htmlRegex.test(str);
    };

    const isLostAndFound = (attributeName: string) => {
        return attributeName === 'lostAndFound__';
    };
    return (
        <>
            {loading ? (
                <div className="tw-h-full tw-flex tw-items-center tw-justify-center">
                    <Loader />
                </div>
            ) : (
                <Table className="tw-w-full comparison-table">
                    <TableHeader>
                        <TableHead></TableHead>
                        <TableHead className="!tw-ml-2">
                            <div className='tw-whitespace-pre-line tw-break-all'>{items[0].primaryKey__}</div>
                            <div className='tw-whitespace-pre-line tw-break-all'>{items[0].category__}</div>
                        </TableHead>
                        <TableHead className="!tw-ml-2">
                            <div className='tw-whitespace-pre-line tw-break-all'>{items[1].primaryKey__}</div>
                            <div className='tw-whitespace-pre-line tw-break-all'>{items[1].category__}</div>
                        </TableHead>
                    </TableHeader>
                    <TableBody>
                        {filteredItems.map((item) => {
                            return <>{item.tableRows}</>;
                        })}
                    </TableBody>
                </Table>
            )}
        </>
    );
};
export default CompareItemsDialogTable;
